import { useMemo } from 'react';
import {
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  useStore,
  type EdgeProps,
  type Edge,
} from '@xyflow/react';
import { makeStyles, tokens } from '@coreai/fluentui-react';
import { CollapseChip } from '../nodes/CollapseChip';
import { getClusterNoun, CLUSTER_STUB_PREFIX, type ClusterKind } from '../helpers/clusterConfig';

/** Below this zoom the toggle is hidden to avoid canvas clutter. */
const MIN_TOGGLE_ZOOM = 0.5;
/** Distance (px) the toggle is nudged from the fan-out handle toward children. */
const TOGGLE_OFFSET = 24;

export type ClusterEdgeData = {
  /** Container kind driving the noun + telemetry. */
  clusterKind: ClusterKind;
  /** Number of direct children collapsing will hide. */
  childCount: number;
  /**
   * Logical collapsed state of the chip. Design-mode toggle edges are always
   * expanded (`false` → "hide" chevron). Monitoring agent toggles are
   * two-state: `true` shows a "Show N tools" affordance (invoked-only view),
   * `false` shows "hide" (all-tools view).
   */
  collapsed?: boolean;
  /** Distinguishes design collapse from execution-mode tool expansion. */
  interactionKind?: 'design-collapse' | 'execution-tools';
  /** Flip the container's collapse/expand state. Receives the next state. */
  onToggle: (nextCollapsed: boolean) => void;
};

export type ClusterEdgeType = Edge<ClusterEdgeData, 'cluster'>;

const useStyles = makeStyles({
  toggle: {
    position: 'absolute',
    pointerEvents: 'all',
    // Sits above the dotted child edges so it is always clickable (the whole
    // reason this replaces the absolute-positioned chip that painted behind
    // the SVG edge layer).
    zIndex: 6,
  },
});

/**
 * The expanded-state collapse control for a container's child cluster.
 *
 * Rendered as a single `cluster` edge whose source is the container and whose
 * target is the container's first child. It draws no visible line of its own
 * (the real dotted child edges already do) — it only portals an always-visible
 * chevron toggle to the fan-out handle root via `EdgeLabelRenderer`, so the
 * control paints above the edge SVG layer and stays clickable. Activating it
 * collapses the container; the N real child edges + nodes then hide and a
 * `ClusterStubNode` count badge takes their place.
 *
 * The toggle is hidden below {@link MIN_TOGGLE_ZOOM} to keep a zoomed-out
 * canvas legible (uses the store transform directly, not `useViewport`, so it
 * does not re-render on every pan).
 */
export const ClusterEdge = ({
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  target,
  data,
  markerEnd,
}: EdgeProps<ClusterEdgeType>) => {
  const styles = useStyles();
  const zoom = useStore((s) => s.transform[2]);

  const [edgePath] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  // Anchor the toggle at the fan-out handle, nudged a little toward the first
  // child so it clears the handle glyph / add-child button that share the root.
  const { x, y } = useMemo(() => {
    const dx = targetX - sourceX;
    const dy = targetY - sourceY;
    const len = Math.hypot(dx, dy);
    if (!len || !Number.isFinite(len)) return { x: sourceX, y: sourceY };
    return {
      x: sourceX + (dx / len) * TOGGLE_OFFSET,
      y: sourceY + (dy / len) * TOGGLE_OFFSET,
    };
  }, [sourceX, sourceY, targetX, targetY]);

  const childCount = data?.childCount ?? 0;
  const clusterKind = data?.clusterKind ?? 'agent';
  const collapsed = data?.collapsed ?? false;
  const noun = getClusterNoun(clusterKind);

  // Draw the dashed connector line ONLY when the edge terminates on an
  // invisible stub anchor (design-mode full collapse — children are hidden, so
  // the line reads as "N hidden items live here"). Monitoring's two-state agent
  // toggle also sets `collapsed`, but targets a *visible* tool node; painting a
  // dashed line there would double up over the real tool edge. Gating on the
  // stub target keeps the two concerns (chip appearance vs. connector) separate.
  const isStubTarget = target?.startsWith(CLUSTER_STUB_PREFIX) ?? false;

  const handleToggle = (nextCollapsed: boolean) => data?.onToggle?.(nextCollapsed);

  // Keep the chip visible whenever it targets the invisible stub anchor — i.e.
  // the container is fully collapsed and its children are hidden, so the chip is
  // the ONLY affordance to reveal them (WCAG discoverability). Otherwise it's a
  // convenience control that can hide on a zoomed-out canvas to reduce clutter.
  const showToggle = childCount > 0 && (isStubTarget || zoom >= MIN_TOGGLE_ZOOM);

  return (
    <>
      {/* Expanded (or monitoring, tools visible): the real dotted child edges
          draw the connections, so this edge stays invisible (it only hosts the
          toggle chip) with a zero-width hit area so it never steals hover /
          click from the real edges beneath it. Collapsed onto a stub: the
          children are hidden, so this edge draws a short dashed line out to the
          invisible anchor, reading as "N hidden items live here". */}
      <BaseEdge
        path={edgePath}
        interactionWidth={0}
        {...(markerEnd ? { markerEnd } : {})}
        style={
          isStubTarget
            ? { stroke: tokens.colorNeutralStroke1, strokeWidth: 1.5, strokeDasharray: '4 4' }
            : { stroke: 'transparent', strokeWidth: 0 }
        }
      />
      {showToggle && (
        <EdgeLabelRenderer>
          <div
            className={styles.toggle}
            style={{
              transform: `translate(-50%, -50%) translate(${x}px, ${y}px)`,
            }}
          >
            <CollapseChip
              collapsed={collapsed}
              count={childCount}
              onToggle={handleToggle}
              nounSingular={noun.singular}
              nounPlural={noun.plural}
            />
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
};

export default ClusterEdge;
